// Utility functions extracted from BrickWeekSchedule
//
// UTC-anchored: a booking's `date` is stored as UTC-midnight of the CENTRE's day, so the centre's
// calendar day is exactly the UTC calendar day of `date`. These helpers used to derive days via
// browser-LOCAL getters (getDay/getDate/setHours), which shifted the whole grid onto the wrong day
// for viewers whose browser TZ differs from the centre's (e.g. under BST). They now delegate to
// `@/lib/calendarDay`, which does all day-math in UTC, so the grid is viewer-timezone independent.
// Names are kept the same so callers are untouched.
import { startOfUtcWeek, utcWeekDates, formatUtcDayHeader, sameUtcDay } from "@/lib/calendarDay";
import { endTimeToMinutes, minutesToHhmm, timeToMinutes } from "@/utils/bookingTime";

export function getStartOfWeek(date: Date) {
  return startOfUtcWeek(date);
}

export function getWeekDates(date: Date) {
  return utcWeekDates(startOfUtcWeek(date));
}

/**
 * Add whole days using UTC calendar arithmetic, consistent with the UTC-anchored `getStartOfWeek`.
 *
 * Nav stepping used to go through `date.setDate(date.getDate() + days)` (LOCAL). Stepping 7 LOCAL
 * days from a UTC-midnight instant crosses the UK spring-forward boundary at a different wall-clock
 * offset (GMT vs BST), so `setDate` lands on 23:00 the previous UTC day instead of 00:00 — which
 * `startOfUtcWeek` then re-floors back onto the SAME week, so "Next week" gets stuck. Stepping in UTC
 * keeps the result at UTC-midnight so the week floor always advances.
 */
export function addUtcDays(date: Date, days: number): Date {
  return new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate() + days,
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds(),
      date.getUTCMilliseconds(),
    ),
  );
}

/** Add whole months using UTC calendar arithmetic, consistent with `addUtcDays`/`getStartOfWeek`. */
export function addUtcMonths(date: Date, months: number): Date {
  return new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth() + months,
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds(),
      date.getUTCMilliseconds(),
    ),
  );
}

export function formatDayHeader(date: Date) {
  return formatUtcDayHeader(date);
}

/**
 * Calculates the top position for a meeting block.
 * @param time - Time in "HHMM" format
 * @param segmentHeight - Height of each segment
 * @param gap - Gap between segments
 * @param segments - (Optional) Array of visible segment times (e.g., ["0600", ...])
 *   If provided, the block index is determined by the index of the time in this array.
 */
export function getMeetingTop(time: string, segmentHeight: number, gap: number, segments?: string[]) {
  const slotSize = segmentHeight + gap;
  const minutes = parseInt(time.slice(2, 4), 10);
  if (segments && Array.isArray(segments)) {
    // Times may fall off the 30-minute grid (e.g. an adhoc booking starting at "1645").
    // Snap down to the enclosing 30-minute segment to find its row, then add the
    // fractional offset for the remaining minutes so off-grid blocks sit correctly.
    // A plain indexOf("1645") would miss and collapse the block to the top of the grid.
    const enclosingSegment = `${time.slice(0, 2)}${minutes >= 30 ? "30" : "00"}`;
    const remainderMinutes = minutes >= 30 ? minutes - 30 : minutes;
    const blockIndex = segments.indexOf(enclosingSegment);
    return blockIndex >= 0 ? (blockIndex + remainderMinutes / 30) * slotSize : 0;
  }
  // Default: all segments visible, 30-minute grid.
  const hours = parseInt(time.slice(0, 2), 10);
  return ((hours * 60 + minutes) / 30) * slotSize;
}

export function getDayIndex(bookingDate: Date, weekDates: Date[]) {
  return weekDates.findIndex((d) => sameUtcDay(d, bookingDate));
}

export function getMeetingHeight(start: string, end: string, segmentHeight: number, gap: number) {
  // time is in "HHMM" format
  const startMinutes = parseInt(start.slice(0, 2), 10) * 60 + parseInt(start.slice(2, 4), 10);
  const endMinutes = parseInt(end.slice(0, 2), 10) * 60 + parseInt(end.slice(2, 4), 10);
  const durationMinutes = endMinutes - startMinutes;
  const blocks = durationMinutes / 30;
  return blocks * (segmentHeight + gap);
}

const SEGMENT_MINUTES = 30;

export interface BookingTileLayout {
  top: number;
  height: number;
  /** Booking starts before the first visible segment (e.g. early hours hidden) — tile is clipped. */
  isClippedAtStart: boolean;
  /** Booking ends after the grid's last visible edge — tile is clipped. */
  isClippedAtEnd: boolean;
}

/**
 * Where a booking tile sits on a 30-minute day grid, or null when it falls entirely outside the
 * visible rows. Shared by every calendar renderer (week, day, standalone day, amenity columns).
 *
 * `visibleSegments` are the rows being drawn (segment START times). When early hours are hidden,
 * the "0000" row is only the show-early toggle, so pass `hiddenEarly` to skip it as a boundary.
 *
 * Two things this gets right that the per-renderer copies did not:
 *   • an end at/before the start (a booking running to midnight, stored "0000") reads as 24:00 —
 *     it used to parse as 0 and the tile vanished;
 *   • the visible range ends at the END of the last row (24:00), not its start (23:30) — tiles
 *     ending after 23:30 were cut short and ones starting at 23:30 were dropped.
 */
export function layoutBookingTile(
  startTime: string | number,
  endTime: string | number,
  visibleSegments: string[],
  hiddenEarly: boolean,
  segmentHeight: number,
): BookingTileLayout | null {
  const boundarySegments =
    hiddenEarly && visibleSegments[0] === "0000" ? visibleSegments.slice(1) : visibleSegments;
  if (boundarySegments.length === 0) return null;

  const start = timeToMinutes(startTime);
  const end = endTimeToMinutes(endTime, startTime);
  const firstVisible = timeToMinutes(boundarySegments[0]);
  const gridEnd = timeToMinutes(boundarySegments[boundarySegments.length - 1]) + SEGMENT_MINUTES;

  if (end <= firstVisible || start >= gridEnd) return null;

  const clampedStart = minutesToHhmm(Math.max(start, firstVisible));
  const clampedEnd = minutesToHhmm(Math.min(end, gridEnd));
  return {
    top: getMeetingTop(clampedStart, segmentHeight, 0, visibleSegments),
    height: getMeetingHeight(clampedStart, clampedEnd, segmentHeight, 0),
    isClippedAtStart: start < firstVisible,
    isClippedAtEnd: end > gridEnd,
  };
}

export function isSameDay(date: Date, compareDate: Date): boolean {
  return sameUtcDay(date, compareDate);
}

// Minimum width (px) each room/amenity column must keep. Below ~40px the column-header
// container query renders neither the horizontal nor the vertical room name, so the name
// silently disappears (only the coloured swatch bar remains). Flooring the per-column width at
// this value keeps every column in at least the vertical-label tier, so room names are always
// visible — the grid scrolls horizontally instead of shrinking columns into illegibility.
export const MIN_COLUMN_WIDTH_PX = 40;

/**
 * Effective width (px) for a single day column-group, honouring the member's chosen
 * `calendarWidth` but never letting individual columns fall below MIN_COLUMN_WIDTH_PX.
 *
 * @param rawDayWidth the member's configured day width (calendarWidth setting)
 * @param columnCount number of room + amenity columns rendered within a day
 */
export function effectiveDayWidth(rawDayWidth: number, columnCount: number): number {
  const columns = Math.max(1, columnCount);
  return Math.max(rawDayWidth, columns * MIN_COLUMN_WIDTH_PX);
}

/**
 * Coloured room-header row height for a given per-column width, mirroring the three-tier rule in
 * ColumnHeaders.tsx: ≥ 96px horizontal label (compact 32px), 40–96px vertical label (80px so the
 * rotated name has room), < 40px bar only (32px).
 */
export function colouredHeaderHeightForColumn(columnWidthPx: number): number {
  return columnWidthPx >= 96 || columnWidthPx < MIN_COLUMN_WIDTH_PX ? 32 : 80;
}

/**
 * Layout for the single-day grid (the only calendar on mobile). Columns share the measured room
 * area but never shrink below MIN_COLUMN_WIDTH_PX — a location with many rooms scrolls
 * horizontally instead of silently dropping the room names. Members can narrow the column count
 * via the room filters.
 *
 * @param columnsAreaPx measured width of the room-column area (already EXCLUDES the time key)
 * @param columnCount number of room + amenity columns
 * @returns contentWidthPx is undefined when the columns fit (render at 100%), otherwise the
 *          widened pixel width the scrolling content must take.
 */
export function dayViewColumnLayout(
  columnsAreaPx: number,
  columnCount: number,
): { contentWidthPx: number | undefined; columnWidthPx: number; colouredHeaderHeightPx: number } {
  const columns = Math.max(1, columnCount);
  if (columnsAreaPx <= 0) {
    return { contentWidthPx: undefined, columnWidthPx: Infinity, colouredHeaderHeightPx: 32 };
  }
  const widened = effectiveDayWidth(columnsAreaPx, columns);
  return {
    contentWidthPx: widened > columnsAreaPx ? widened : undefined,
    columnWidthPx: widened / columns,
    colouredHeaderHeightPx: colouredHeaderHeightForColumn(widened / columns),
  };
}

// Utility to check if screen is xl or above (1280px)
export function isXLScreen() {
  if (typeof window !== "undefined") {
    return window.matchMedia("(min-width: 1280px)").matches;
  }
  return false;
}

/**
 * Returns the minimum date users can navigate to (1 year in the past)
 */
export function getMinNavigationDate(): Date {
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
  oneYearAgo.setHours(0, 0, 0, 0);
  return oneYearAgo;
}
